import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import {
  BodyText,
  IconLink,
  TextLink,
  TextSpace,
  theme,
  useSafeNavigation,
  useSafeRouter,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useIsExpoRouterEnabled } from '@order/shared/hooks';

import { useCustomerData } from '../../../context/Customer/hooks';
import {
  useLastInteractedStore,
  useNavigateToLastInteractedStore,
} from '../../../context/LastInteractedStore';

export const OrdersHeader = (props: OrdersHeaderProps) => {
  const { isReorderEnabled } = props;
  const { formatMessage } = useIntl();

  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled');

  const { customer } = useCustomerData();
  const lastInteractedStore = useLastInteractedStore();

  const { addressId, restaurantName } = lastInteractedStore ?? {};

  const addressName = customer?.addresses?.find((address) => address.id === addressId)?.name;

  const location = addressName ?? restaurantName;

  const navigation = useSafeNavigation();
  const router = useSafeRouter();
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const navigateToLastInteractedStore = useNavigateToLastInteractedStore();

  const goBack = useCallback(() => {
    if (isExpoRouterEnabled) {
      if (router?.canGoBack()) {
        router.back();
        return;
      }

      router?.navigate('/account');

      return;
    }

    if (navigation?.canGoBack()) {
      navigation.goBack();

      return;
    }

    navigation?.navigate('MainTabs', {
      screen: 'AccountTab',
      params: { screen: 'AccountMenu' },
    });
  }, [isExpoRouterEnabled, router, navigation]);

  const shouldRenderReorderTitle = isReorderEnabled && location && isInStoreOrdersEnabled;

  return (
    <SafeAreaView edges={['top']} style={styles.header}>
      <IconLink width={40} height={40} name="IconArrowLeft" palette="muted" onPress={goBack} />

      {shouldRenderReorderTitle ? (
        <View style={styles.titleContainer}>
          <BodyText numberOfLines={1} sizeMatch={['22']} style={styles.title}>
            <FormattedMessage {...messages.title} />

            <TextSpace />

            <TextLink
              sizeMatch={['22']}
              accessibilityRole="link"
              accessibilityLabel={formatMessage(messages.accessibilityLabel)}
              style={styles.link}
              onPress={navigateToLastInteractedStore}
            >
              {location}
            </TextLink>
          </BodyText>
        </View>
      ) : null}

      <View style={styles.rightIcon} />
    </SafeAreaView>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
    paddingVertical: theme.spacing['3'],
    paddingHorizontal: theme.spacing['4'],
    minHeight: 64,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    textAlign: 'center',
  },
  link: {
    color: theme.colors.KALE,
  },
  rightIcon: {
    width: 40,
    height: 40,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Reorder to',
    description: 'Account | Orders | Reorder Title | Title',
  },
  accessibilityLabel: {
    defaultMessage: 'Opens menu for location',
    description: 'Account | Orders | Reorder Title | Accessibility label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrdersHeaderProps = Readonly<{
  isReorderEnabled: boolean;
}>;
